import { Link } from 'react-router-dom';
import useInView from '../../../hooks/useInView';

const SERVICES = [
  {
    icon: 'ri-search-eye-line',
    title: 'Search Engine Optimization',
    desc: 'Improve your visibility on search engines through technical audits, keyword strategy, and content planning.',
    tag: 'SEO',
    iconColor: 'text-brand-blue',
    tagColor: 'text-brand-blue-dim',
    iconBg: 'bg-blue-50 border-blue-100',
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    title: 'Performance Marketing',
    desc: 'Data-driven paid campaigns designed to drive measurable results — leads, sign-ups, and sales.',
    tag: 'Performance',
    iconColor: 'text-brand-blue',
    tagColor: 'text-brand-blue',
    iconBg: 'bg-blue-50 border-blue-100',
  },
  {
    icon: 'ri-instagram-line',
    title: 'Social Media Marketing',
    desc: 'Strategy, content creation, and community management across Instagram, LinkedIn, and Facebook.',
    tag: 'Social',
    iconColor: 'text-pink-500',
    tagColor: 'text-pink-600',
    iconBg: 'bg-pink-50 border-pink-100',
  },
  {
    icon: 'ri-broadcast-line',
    title: 'Media Buying',
    desc: 'Strategic ad placements across digital channels to reach your audience at the right time and context.',
    tag: 'Media',
    iconColor: 'text-violet-500',
    tagColor: 'text-violet-600',
    iconBg: 'bg-violet-50 border-violet-100',
  },
];

function ServiceCard({ service, delay }: { service: typeof SERVICES[0]; delay: number }) {
  const { ref, visible } = useInView(0.1);
  return (
    <div
      ref={ref}
      className="group bg-white border border-gray-100 hover:border-gray-200 rounded-2xl p-7 transition-all duration-300 cursor-default"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.55s ease ${delay}ms, transform 0.55s ease ${delay}ms`,
      }}
    >
      <div className="flex items-start justify-between mb-6">
        <div className={`w-11 h-11 flex items-center justify-center rounded-xl border ${service.iconBg}`}>
          <i className={`${service.icon} text-xl ${service.iconColor}`}></i>
        </div>
        <span className={`text-[10px] font-bold uppercase tracking-widest ${service.tagColor} border border-current/20 px-2.5 py-1 rounded-full opacity-80`}>
          {service.tag}
        </span>
      </div>
      <h3 className="font-display font-semibold text-gray-900 text-[15px] mb-2.5 leading-snug">
        {service.title}
      </h3>
      <p className="text-gray-500 text-[13px] leading-relaxed">
        {service.desc}
      </p>
    </div>
  );
}

export default function ServicesPreview() {
  const { ref: headRef, visible: headVisible } = useInView(0.2);

  return (
    <section className="py-12 md:py-16 bg-gray-50">
      <div className="max-w-site mx-auto px-4 sm:px-6">
        <div
          ref={headRef}
          className="mb-8 md:mb-10"
          style={{
            opacity: headVisible ? 1 : 0,
            transform: headVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-brand-blue mb-5">
            What We Do
          </span>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
            <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-[2.6rem] text-gray-900 leading-tight max-w-lg">
              Services Built for<br />Real Business Growth
            </h2>
            <p className="text-gray-500 text-[15px] max-w-sm leading-relaxed">
              A focused set of digital marketing services — each one designed to deliver clear, practical outcomes.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {SERVICES.map((service, i) => (
            <ServiceCard key={service.title} service={service} delay={i * 80} />
          ))}
        </div>

        <div className="flex justify-center">
          <Link
            to="/services"
            className="btn-outline px-6 py-3"
          >
            View All 7 Services
            <i className="ri-arrow-right-line text-sm"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
